import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, photoUrl, formatApiErrorDetail } from "@/api";
import { PriorityBadge } from "@/components/Badges";
import { IncidentMiniMap } from "@/components/IncidentMaps";
import { Lightbox } from "@/components/Lightbox";
import { ROLE_LABELS, fmtTime, fmtMinutes } from "@/lib/constants";
import { ArrowLeft, Printer, Loader2, ShieldCheck, Building2, Truck, FileBarChart } from "lucide-react";
import { toast } from "sonner";

export default function IncidentReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lb, setLb] = useState(null);
  const [incMap, setIncMap] = useState(null);
  const [legend, setLegend] = useState(null);
  useEffect(() => { api.get(`/incidents/${id}`).then(({ data }) => setIncMap(data)).catch(() => {}); }, [id]);
  useEffect(() => { api.get("/settings/color-legend").then(({ data }) => setLegend(data.legend || null)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reports/incident/${id}`);
      setReport(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
        <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-600" /> AI stelt het incidentrapport op…
      </div>
    );
  }
  if (!report) return <div className="text-slate-400">Geen rapport beschikbaar.</div>;

  const inc = report.incident;
  const s = report.stats;

  return (
    <div className="space-y-5" data-testid="incident-report-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <button data-testid="report-back" onClick={() => navigate(`/incidenten/${id}`)} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Terug naar incident
        </button>
        <button data-testid="print-incident-report" onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
          <Printer className="h-4 w-4" /> Afdrukken / PDF
        </button>
      </div>

      <div className="print-page mx-auto max-w-4xl space-y-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-200 pb-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600"><ShieldCheck className="h-5 w-5 text-white" /></div>
            <div>
              <div className="font-display text-lg font-bold text-slate-900">Incidentrapport · {inc.ref}</div>
              <div className="text-sm text-slate-500">{inc.title}</div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-400">
            <div>Gegenereerd: {fmtTime(report.generated_at)}</div>
            <div>Door: {report.generated_by}</div>
          </div>
        </div>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
          <PriorityBadge priority={inc.priority} />
          <span className="rounded bg-slate-100 px-2 py-0.5 font-medium">{inc.incident_type?.name}</span>
          <span className="inline-flex items-center gap-1"><Building2 style={{ width: 14, height: 14 }} />{inc.building?.name}</span>
          <span className="inline-flex items-center gap-1"><Truck style={{ width: 14, height: 14 }} />{inc.vehicles?.map((v) => v.callsign).join(", ") || "geen"}</span>
          <span>{inc.status === "closed" ? "Gesloten" : "Open"}</span>
        </div>
        {inc.description && <p className="text-sm text-slate-600">{inc.description}</p>}

        {incMap && (incMap.building || incMap.location_lat != null) && (
          <div className="print-break" data-testid="report-map">
            <div className="mb-2 text-sm font-bold text-slate-900">Kaart & tekening</div>
            <IncidentMiniMap
              lat={incMap.building ? incMap.building.lat : incMap.location_lat}
              lng={incMap.building ? incMap.building.lng : incMap.location_lng}
              label={incMap.building ? incMap.building.name : (incMap.location_label || "Locatie")}
              routes={(incMap.approach_lines && incMap.approach_lines.some((r) => r && r.length)) ? incMap.approach_lines : (incMap.building?.approach_routes || [])}
              tweezijdig={!!incMap.tweezijdig_aanrijden}
              drawings={(incMap.map_drawings_archived && incMap.map_drawings_archived.features && incMap.map_drawings_archived.features.length) ? incMap.map_drawings_archived : incMap.map_drawings}
              legend={legend}
            />
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 print-break">
          {[
            { l: "Doorlooptijd", v: fmtMinutes(s.duration_min) },
            { l: "Responstijd", v: fmtMinutes(s.response_min) },
            { l: "Notities", v: s.notes_count },
            { l: "Foto's", v: s.photos_count },
            { l: "Voertuigen", v: s.vehicles_count },
          ].map((k) => (
            <div key={k.l} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{k.l}</div>
              <div className="mt-1 font-display text-xl font-bold text-slate-900">{k.v}</div>
            </div>
          ))}
        </div>

        {/* AI summary */}
        <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-5 print-break">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-900"><FileBarChart className="h-4 w-4 text-blue-600" /> Samenvatting (AI)</div>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{report.ai_summary}</div>
        </div>

        {/* Evaluation */}
        {report.evaluation && (report.evaluation.went_well || report.evaluation.learning_goals) && (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-5 print-break" data-testid="report-evaluation">
            <div className="mb-2 text-sm font-bold text-slate-900">Evaluatie</div>
            {report.evaluation.went_well && (<div className="mb-2"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wat ging goed</div><div className="whitespace-pre-wrap text-sm text-slate-700">{report.evaluation.went_well}</div></div>)}
            {report.evaluation.learning_goals && (<div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Leerdoelen</div><div className="whitespace-pre-wrap text-sm text-slate-700">{report.evaluation.learning_goals}</div></div>)}
          </div>
        )}

        {/* Photos */}
        {report.photos.length > 0 && (
          <div className="print-break">
            <div className="mb-2 text-sm font-bold text-slate-900">Bijlage · Foto's ({report.photos.length})</div>
            <div className="grid grid-cols-3 gap-3">
              {report.photos.map((p, idx) => (
                <button key={p.id} type="button" data-testid={`report-photo-${p.id}`} onClick={() => setLb(idx)} className="no-print-hover group relative overflow-hidden rounded-xl border border-slate-200 text-left">
                  <img src={photoUrl(p.id)} alt={p.original_filename} className="aspect-square w-full object-cover" />
                  {p.source === "live_snapshot" && <span className="absolute left-1.5 top-1.5 rounded bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white">LIVE-SNAPSHOT</span>}
                  <div className="px-2 py-1 text-[10px] text-slate-400">{p.uploaded_by} · {fmtTime(p.created_at)}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notes appendix */}
        <div className="print-break">
          <div className="mb-2 text-sm font-bold text-slate-900">Bijlage · Kladblokregels ({report.notes.length})</div>
          {report.notes.length === 0 ? <div className="text-sm text-slate-400">Geen notities.</div> : (
            <div className="space-y-2">
              {report.notes.map((n) => (
                <div key={n.id} className="rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-sm">
                  <div className="mb-0.5 flex items-center gap-2 text-xs">
                    <span className="font-semibold text-slate-900">{n.author}</span>
                    <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600">{ROLE_LABELS[n.author_role]}</span>
                    <span className="text-slate-400">{fmtTime(n.created_at)}</span>
                  </div>
                  <div className="whitespace-pre-wrap text-slate-700">{n.text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {lb !== null && <Lightbox photos={report.photos} startIndex={lb} onClose={() => setLb(null)} />}
    </div>
  );
}
